import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { API_BASE } from "../services/api.js";

/**
 * <cos-login> — Login screen.
 *
 * Stage 27 retarget: posts username + password to /api/v1/auth/login,
 * which proxies to microservice-auth /v2/tokens/staff. The refresh
 * token rides an httpOnly cookie set by the backend; the SPA never
 * touches it directly. The access token comes back in the JSON body
 * and we stash it in sessionStorage (same key Stage 22b used) so the
 * existing api.ts bearer-injection code works unchanged.
 *
 * Stage 19g — split-panel layout (charcoal brand panel + white form
 * column, collapsing to centred-card at <768px) preserved.
 *
 * The DEV ONLY badge is preserved — useful for local builds where the
 * user wants to be reminded they're hitting the local docker auth on
 * :3080 rather than the deployed service. Identical form shape in dev
 * and prod; the ONLY difference is the badge.
 */
@customElement("cos-login")
export class CosLogin extends LitElement {
	@state() private _username = "";
	@state() private _password = "";
	@state() private _error = "";
	@state() private _loading = false;

	static styles = css`
		:host {
			display: grid;
			grid-template-columns: 1fr 1fr;
			min-height: 100vh;
			background: var(--cos-surface);
		}

		/* ── Brand panel (left, desktop only) ────────────────────── */
		.brand-panel {
			background: var(--cos-charcoal);
			color: var(--cos-surface);
			display: flex;
			flex-direction: column;
			justify-content: center;
			padding: var(--cos-space-12) var(--cos-space-10);
		}
		.brand-logo {
			display: block;
			/* The panel is a flex column; without this the box stretches
			   full width and the artwork centres inside it. */
			align-self: flex-start;
			height: 40px;
			width: auto;
			max-width: 100%;
			margin-bottom: var(--cos-space-8);
		}
		.tagline {
			font-size: var(--cos-font-size-3xl);
			font-weight: 600;
			line-height: 1.2;
			margin: 0 0 var(--cos-space-4) 0;
			max-width: 18ch;
		}
		.tagline-sub {
			color: var(--cos-border);
			font-size: var(--cos-font-size-base);
			line-height: 1.5;
			max-width: 36ch;
		}

		/* ── Form panel (right) ──────────────────────────────────── */
		.form-panel {
			display: flex;
			align-items: center;
			justify-content: center;
			padding: var(--cos-space-8);
		}
		.form-card {
			width: 100%;
			max-width: 420px;
		}

		.heading {
			font-size: var(--cos-font-size-2xl);
			color: var(--cos-text-primary);
			font-weight: 700;
			margin: 0 0 var(--cos-space-2) 0;
		}
		.heading-sub {
			color: var(--cos-text-secondary);
			font-size: var(--cos-font-size-base);
			margin: 0 0 var(--cos-space-6) 0;
		}

		/* Mobile-only inline brand (the brand-panel is hidden). */
		@media (max-width: 768px) {
			.mobile-brand {
				display: block;
				height: 24px;
				width: auto;
				margin-bottom: var(--cos-space-5);
			}
		}
		@media (min-width: 769px) {
			.mobile-brand { display: none; }
		}

		.field {
			margin-bottom: var(--cos-space-4);
		}
		label {
			display: block;
			font-size: var(--cos-font-size-sm);
			font-weight: 500;
			color: var(--cos-text-secondary);
			margin-bottom: var(--cos-space-1);
		}
		input {
			width: 100%;
			padding: var(--cos-space-2) var(--cos-space-3);
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-sm);
			font-family: var(--cos-font-family);
			font-size: var(--cos-font-size-base);
			color: var(--cos-text-primary);
			transition: border-color 0.2s;
			box-sizing: border-box;
		}
		input:focus {
			outline: none;
			border-color: var(--cos-brand-500);
			box-shadow: 0 0 0 3px var(--cos-brand-50);
		}

		button {
			width: 100%;
			padding: var(--cos-space-2) var(--cos-space-4);
			background: var(--cos-brand-700);
			color: var(--cos-surface);
			border: none;
			border-radius: var(--cos-radius-sm);
			font-family: var(--cos-font-family);
			font-size: var(--cos-font-size-base);
			font-weight: 600;
			cursor: pointer;
			transition: background 0.2s;
			margin-top: var(--cos-space-4);
		}
		button:hover { background: var(--cos-brand-800); }
		button:disabled { opacity: 0.6; cursor: not-allowed; }

		/* DEV ONLY badge — gated on import.meta.env.MODE in render(). */
		.dev-badge {
			display: inline-block;
			background: var(--cos-status-warning-wash);
			color: var(--cos-status-warning-text);
			padding: 2px 10px;
			border-radius: 999px;
			font-size: var(--cos-font-size-2xs);
			font-weight: 700;
			letter-spacing: 0.06em;
			text-transform: uppercase;
			margin-bottom: var(--cos-space-3);
		}

		.prod-signin {
			display: block;
			width: 100%;
			padding: var(--cos-space-3) var(--cos-space-4);
			background: var(--cos-brand-700);
			color: var(--cos-surface);
			text-align: center;
			text-decoration: none;
			border-radius: var(--cos-radius-sm);
			font-weight: 600;
			margin-top: var(--cos-space-4);
			box-sizing: border-box;
		}
		.prod-signin:hover { background: var(--cos-brand-800); }
		.prod-signin:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: 2px;
		}
		.prod-help {
			margin-top: var(--cos-space-3);
			color: var(--cos-text-secondary);
			font-size: var(--cos-font-size-sm);
			text-align: center;
		}

		.error {
			background: var(--cos-status-danger-wash);
			color: var(--cos-status-danger-text);
			padding: var(--cos-space-2) var(--cos-space-3);
			border-radius: var(--cos-radius-sm);
			font-size: var(--cos-font-size-sm);
			margin-bottom: var(--cos-space-4);
		}

		/* Mobile + narrow tablet: collapse to centred card. The
		   mobile rules live at the END of the stylesheet so they
		   override the desktop unconditional rules above (CSS
		   cascading wins on later sibling rules at equal specificity). */
		@media (max-width: 768px) {
			:host {
				grid-template-columns: 1fr;
				background: var(--cos-surface-raised);
				place-items: center;
			}
			.brand-panel { display: none; }
			.form-panel {
				width: 100%;
				max-width: 420px;
				padding: var(--cos-space-6);
				/* Shadow styles don't inherit the light-DOM box-sizing reset —
				   without this, 100% + the padding overflows a 390px phone. */
				box-sizing: border-box;
			}
			.form-card {
				background: var(--cos-surface);
				border-radius: var(--cos-radius-md);
				box-shadow: var(--cos-shadow-lg);
				padding: var(--cos-space-8) var(--cos-space-6);
			}
		}
	`;

	// Stage 19g: import.meta.env.DEV is Vite's static-replaced flag —
	// true in dev, false in prod. The DEV ONLY badge below is gated
	// on it, so production bundles tree-shake the badge entirely.
	private _isDev(): boolean {
		return import.meta.env.DEV;
	}

	private async _handleSubmit(e: Event) {
		e.preventDefault();
		this._error = "";
		this._loading = true;
		try {
			// credentials: 'include' so the httpOnly refresh cookie set by
			// the backend (forwarded from microservice-auth) is accepted
			// by the browser. Without this, the cookie is silently dropped.
			const response = await fetch(`${API_BASE}/auth/login`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ username: this._username, password: this._password }),
			});
			if (!response.ok) {
				let message = "Sign-in failed";
				if (response.status === 401) {
					message = "Invalid email or password";
				} else if (response.status === 503) {
					message = "Authentication service is unavailable. Try again in a moment.";
				} else {
					try {
						const body = await response.json();
						message = body.message || body.error || message;
					} catch { /* keep default */ }
				}
				throw new Error(message);
			}
			const data = await response.json();
			// The sign-in service can ask for a second step instead of
			// issuing a token (a 200 with no access_token). This screen has
			// no second-step form yet, so say so rather than dispatch a
			// login with no token.
			if (data.mfa_required || !data.access_token) {
				throw new Error(
					"Your account needs a second sign-in step that this screen cannot complete yet. Contact your administrator.",
				);
			}
			// Refresh token rides the httpOnly cookie; only the access
			// token + ttl land in the body. Dispatch the login event so
			// app.ts can stash the token and navigate to the post-login
			// destination.
			this.dispatchEvent(new CustomEvent("cos:auth:login", {
				bubbles: true, composed: true,
				detail: {
					token: data.access_token,
					expires_in: data.expires_in,
				},
			}));
		} catch (err) {
			this._error = err instanceof Error ? err.message : "Sign-in failed";
		} finally {
			this._loading = false;
		}
	}

	render() {
		const dev = this._isDev();
		return html`
			<aside class="brand-panel" aria-hidden="true">
				<img class="brand-logo" src="/images/brand/communico-one-logo-light.svg" alt="">
				<h2 class="tagline">The internal operations platform for libraries.</h2>
				<p class="tagline-sub">
					Tickets, partners, projects, and knowledge: one workspace your
					team and library admins both work in.
				</p>
			</aside>

			<section class="form-panel">
				<main class="form-card" aria-labelledby="login-heading">
					<img class="mobile-brand" src="/images/brand/communico-one-logo-dark.svg" alt="Communico One">
					<h1 id="login-heading" class="heading">Sign in</h1>
					<p class="heading-sub">${dev ? "Local development build." : "Welcome back."}</p>

					${this._error ? html`<div class="error" role="alert">${this._error}</div>` : ""}

					${dev ? html`<div class="dev-badge" aria-label="Development environment">DEV ONLY</div>` : ""}

					<form @submit=${this._handleSubmit}>
						<div class="field">
							<label for="username">Email</label>
							<input id="username" type="email" autocomplete="email" required
								.value=${this._username}
								@input=${(e: InputEvent) => this._username = (e.target as HTMLInputElement).value}>
						</div>
						<div class="field">
							<label for="password">Password</label>
							<input id="password" type="password" autocomplete="current-password" required
								.value=${this._password}
								@input=${(e: InputEvent) => this._password = (e.target as HTMLInputElement).value}>
						</div>
						<button type="submit" ?disabled=${this._loading}>
							${this._loading ? "Signing in..." : "Sign in"}
						</button>
					</form>
				</main>
			</section>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"cos-login": CosLogin;
	}
}
